import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { useState } from 'react';

import type { FC, ReactNode } from 'react';

import { AUTH_ROUTES } from '@/constants/auth';
import { ERROR_FALLBACK } from '@/constants/errors';
import { errorLog } from '@/lib/errorLog';
import { notify } from '@/lib/notify';
import { ERROR_CODES } from '@/services/errors';
import { logger } from '@/services/logger';
import { resolveErrorCopy } from '@/utils/errorCopy';
import { toEntityID } from '@/utils/errorLog';
import { toErrorToast, toSuccessToast } from '@/utils/notifications';

const QUERY_STALE_TIME_MS = 30_000;
const MAX_READ_RETRIES = 1;

const STAFF_INACTIVE_CODE = 'staff_inactive';

const AUTH_FAILURE_CODES: string[] = ['401', ERROR_CODES.unauthorized, STAFF_INACTIVE_CODE];

type TQueryProviderProps = {
  children: ReactNode;
};

type TFailure = Error & { where?: string; detail?: string };

const describe = (error: Error): unknown => {
  const failure = error as TFailure;

  return { code: failure.message, where: failure.where, detail: failure.detail };
};

const isReported = (error: Error): boolean => Boolean((error as TFailure).where);

const isAuthFailure = (error: Error): boolean => AUTH_FAILURE_CODES.includes(error.message);

const toFailureWhere = (error: Error): string | undefined => (error as TFailure).where;

const retryRead = (failureCount: number, error: Error): boolean => {
  return failureCount < MAX_READ_RETRIES && error.message === ERROR_CODES.network;
};

const leaveForLogin = (error: Error): void => {
  if (!AUTH_FAILURE_CODES.includes(error.message) || typeof window === 'undefined') {
    return;
  }

  if (window.location.pathname === AUTH_ROUTES.login) {
    return;
  }

  window.location.assign(AUTH_ROUTES.login);
};

const createQueryClient = () => new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!isReported(error)) {
        logger.error(`query failed — ${JSON.stringify(query.queryKey)}`, describe(error));
      }

      if (!isAuthFailure(error)) {
        errorLog.report({
          source: 'query',
          where: toFailureWhere(error),
          key: query.queryKey,
          code: error.message,
          message: resolveErrorCopy(error.message) ?? ERROR_FALLBACK,
        });
      }

      leaveForLogin(error);
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      const title = toSuccessToast(mutation.meta);

      if (title) {
        notify.success(title);
      }
    },
    onError: (error, variables, _context, mutation) => {
      if (!isReported(error)) {
        logger.error(`mutation failed — ${mutation.options.mutationKey?.join('.') ?? 'unkeyed'}`, describe(error));
      }

      const title = toErrorToast(mutation.meta, error);

      if (!isAuthFailure(error)) {
        if (title) {
          notify.error(title);
        }

        errorLog.report({
          source: 'mutation',
          where: toFailureWhere(error),
          key: mutation.options.mutationKey,
          code: error.message,
          entityID: toEntityID(variables),
          message: title ?? resolveErrorCopy(error.message) ?? ERROR_FALLBACK,
        });
      }

      leaveForLogin(error);
    },
  }),
  defaultOptions: {
    queries: {
      retry: retryRead,
      refetchOnWindowFocus: false,
      staleTime: QUERY_STALE_TIME_MS,
    },
    mutations: { retry: false },
  },
});

const QueryProvider: FC<TQueryProviderProps> = ({ children }) => {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};

export type {
  TQueryProviderProps,
};
export {
  QueryProvider,
};
